/*-----------------------------------------------------------------------------/
/ Custom Theme JS
/-----------------------------------------------------------------------------*/

// Insert any custom theme js here...

/*! Hapbidets — Optimistic accurate-pending cart open.
 *  Opens the cart drawer the instant the user clicks Add, shows the exact item
 *  they added (real image + title), and masks ONLY the subtotal / discounts /
 *  free-shipping bar behind a spinner until the real numbers land.
 *
 *  Reveals the real totals the moment the cart actually re-renders (right after
 *  the cart fetch) — NOT after the drawer's "Complete Your Bathroom"
 *  recommendations refetch, which the store otherwise waits on. That extra
 *  round-trip only happens when a brand-new product type is added (e.g. the
 *  fragrance), which is why those felt slower.
 *
 *  Self-contained: hooks the cart store's lifecycle events + watches the cart
 *  DOM, so it covers every add entry point (PDP, quick-shop, in-drawer upsell)
 *  with no changes to the theme's other JS. Safe to remove: delete everything
 *  below this banner.
 */
(function () {
  "use strict";

  var PENDING_CLASS = "cart-item--optimistic-pending";
  var UPDATING_CLASS = "is-cart-updating";
  var DRAWER_SELECTOR = ".modal--cart, .popup--cart-notification";
  var MIN_DWELL_MS = 120; // ignore drawer-open DOM noise before accepting a render

  var captured = new Map(); // variantId -> { title, image, variant }
  var skipNext = false;     // true when the triggering add redirects (don't pop drawer)
  var active = false;       // a pending overlay is currently shown
  var settling = false;     // a settle/clear is already scheduled
  var observer = null;      // watches the cart DOM for the real re-render
  var startedAt = 0;

  function injectStyles() {
    if (document.getElementById("optimistic-cart-styles")) return;
    var css =
      "." + PENDING_CLASS + "{animation:optimistic-fade .15s ease-in}" +
      "@keyframes optimistic-fade{from{opacity:0}to{opacity:1}}" +
      ".optimistic-spinner{display:inline-block;width:16px;height:16px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:optimistic-spin .6s linear infinite;vertical-align:middle;opacity:.45}" +
      "@keyframes optimistic-spin{to{transform:rotate(360deg)}}" +
      "." + UPDATING_CLASS + " cart-total{visibility:hidden;position:relative}" +
      "." + UPDATING_CLASS + " cart-total::after{content:'';position:absolute;right:0;top:50%;width:14px;height:14px;margin-top:-7px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;visibility:visible;animation:optimistic-spin .6s linear infinite;opacity:.45}" +
      "free-shipping-bar." + UPDATING_CLASS + "{opacity:.4;transition:opacity .15s}" +
      "cart-item-discounts." + UPDATING_CLASS + "{visibility:hidden}";
    var style = document.createElement("style");
    style.id = "optimistic-cart-styles";
    style.textContent = css;
    document.head.appendChild(style);
  }

  function harvest(form) {
    var data = { title: null, image: null, variant: null };
    var scope = form.closest("quick-shop, .quick-shop, .modal, .card, .product, [class*='product']") || document;
    var titleEl = scope.querySelector("h1, h2, .product-title, [class*='title']") || document.querySelector("h1");
    if (titleEl) data.title = titleEl.textContent.trim().slice(0, 80);
    var img = scope.querySelector("img") || document.querySelector(".product__media img, [class*='product'] img");
    if (img) data.image = img.currentSrc || img.getAttribute("src") || null;
    var checkedLabel = form.querySelector("input[type='radio']:checked + label");
    if (checkedLabel) data.variant = checkedLabel.textContent.trim().slice(0, 40);
    return data;
  }

  document.addEventListener(
    "submit",
    function (e) {
      var form = e.target;
      if (!form || form.tagName !== "FORM") return;
      var pf = form.closest("product-form");
      if (!pf) return;
      skipNext = pf.hasAttribute("redirect-on-add");
      var idInput = form.querySelector("[name='id']");
      if (idInput) captured.set(String(idInput.value), harvest(form));
    },
    true
  );

  function getDrawer() { return document.querySelector(DRAWER_SELECTOR); }
  function openDrawer(drawer) {
    if (!drawer) return false;
    if (drawer.shown) return true;
    if (typeof drawer.open === "function") { drawer.open(); return true; }
    return false;
  }

  function buildPendingRow(display) {
    var el = document.createElement("div");
    el.className = "cart-item cart-item--media-ratio-1:1 cart-item--media-crop " + PENDING_CLASS;
    var media = display && display.image
      ? '<div class="cart-item__media"><img src="' + display.image + '" alt="" loading="eager" style="width:100%;height:100%;object-fit:cover"></div>'
      : '<div class="cart-item__media"></div>';
    var title = display && display.title ? display.title : "Adding to cart\u2026";
    var variant = display && display.variant ? '<div class="cart-item__variant u-small">' + display.variant + "</div>" : "";
    el.innerHTML =
      media +
      '<div class="cart-item__details">' +
        '<div class="cart-item__title">' + title + "</div>" +
        variant +
        '<div class="cart-item__price"><span class="optimistic-spinner"></span></div>' +
      "</div>" +
      '<div class="cart-item__qty"><span class="optimistic-spinner"></span></div>';
    return el;
  }

  function watchForRealRender(drawer) {
    if (observer) { observer.disconnect(); observer = null; }
    var list = drawer.querySelector("cart-items.cart__items, cart-items, .cart__items");
    var total = drawer.querySelector(".cart__total");
    var targets = [];
    if (list) targets.push(list);
    if (total) targets.push(total);
    if (!targets.length) return;
    observer = new MutationObserver(function () {
      if (performance.now() - startedAt < MIN_DWELL_MS) return;
      settle();
    });
    targets.forEach(function (t) {
      observer.observe(t, { childList: true, subtree: true, characterData: true });
    });
  }

  function applyPending() {
    var drawer = getDrawer();
    if (!openDrawer(drawer)) return;
    active = true;
    startedAt = performance.now();

    var list = drawer.querySelector("cart-items.cart__items, cart-items, .cart__items");
    if (list) {
      var displays = captured.size ? Array.from(captured.values()) : [null];
      displays.forEach(function (d) { list.insertBefore(buildPendingRow(d), list.firstChild); });
    }

    var subtotal = drawer.querySelector(".cart__total");
    if (subtotal) subtotal.classList.add(UPDATING_CLASS);
    var fsb = drawer.querySelector("free-shipping-bar");
    if (fsb) fsb.classList.add(UPDATING_CLASS);
    drawer.querySelectorAll("cart-item-discounts").forEach(function (d) { d.classList.add(UPDATING_CLASS); });

    watchForRealRender(drawer);
  }

  function settle() {
    if (!active || settling) return;
    settling = true;
    requestAnimationFrame(function () { requestAnimationFrame(clearPending); });
  }

  function clearPending() {
    active = false;
    settling = false;
    if (observer) { observer.disconnect(); observer = null; }
    captured.clear();
    skipNext = false;
    document.querySelectorAll("." + PENDING_CLASS).forEach(function (n) { n.remove(); });
    document.querySelectorAll("." + UPDATING_CLASS).forEach(function (n) { n.classList.remove(UPDATING_CLASS); });
  }

  document.addEventListener("on:cart:loading", function (e) {
    if (!e.detail || e.detail.type !== "ADD") return;
    if (skipNext) { skipNext = false; return; }
    injectStyles();
    applyPending();
  });

  function onSettled(e) {
    if (!e.detail || e.detail.type !== "ADD") return;
    if (!active) { captured.clear(); skipNext = false; return; }
    settle();
  }
  document.addEventListener("on:cart:loaded", onSettled);
  document.addEventListener("on:cart:failed", onSettled);
})();
